(() => {
  const SELECTOR = "[data-vendors-mega-menu]";
  const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  const DEFAULT_LETTER = "A";
  const dataCache = new Map();

  const escapeHTML = (value) =>
    String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");

  const normalizeVendors = (rows) => {
    if (!Array.isArray(rows)) return [];

    return rows
      .map((item) => ({
        name: String(item?.name || "").trim(),
        url: String(item?.url || "").trim(),
      }))
      .filter((item) => item.name && item.url)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, {sensitivity: "base"}));
  };

  const fetchVendors = async (src) => {
    if (!src) throw new Error("Missing vendors.json source URL.");
    if (dataCache.has(src)) return dataCache.get(src);

    const load = (url) =>
      fetch(url, {credentials: "same-origin"}).then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load ${url} (${response.status})`);
        }
        return response.json();
      });

    const fallbackSrc = src.replace(/vendors\.json(\?.*)?$/i, "vendor.json$1");
    const request = load(src)
      .catch((error) => {
        if (fallbackSrc === src) throw error;
        return load(fallbackSrc);
      })
      .then(normalizeVendors);

    dataCache.set(src, request);
    return request;
  };

  class VendorsMegaMenu {
    constructor(root) {
      this.root = root;
      this.src = root.dataset.vendorsSrc || "";
      this.emptyText = root.dataset.emptyText || "No vendors available";
      this.searchEl = root.querySelector("[data-vendors-search]");
      this.backEl = root.querySelector("[data-vendors-back]");
      this.azEl = root.querySelector("[data-vendors-az]");
      this.scrollEl = root.querySelector("[data-vendors-scroll]");
      this.contentEl = root.querySelector("[data-vendors-content]");
      this.statusEl = root.querySelector("[data-vendors-status]");

      this.allVendors = [];
      this.vendorsByLetter = new Map();
      this.filteredVendors = [];
      this.searchQuery = "";
      this.activeLetter = DEFAULT_LETTER;
      this.renderRaf = null;

      this.handleSearch = this.handleSearch.bind(this);
      this.handleAlphabetClick = this.handleAlphabetClick.bind(this);
      this.handleBackClick = this.handleBackClick.bind(this);
      this.handleMenuToggle = this.handleMenuToggle.bind(this);
    }

    async init() {
      if (!this.searchEl || !this.azEl || !this.scrollEl || !this.contentEl) return;

      this.bindEvents();

      try {
        this.allVendors = await fetchVendors(this.src);
      } catch (error) {
        console.error("[Vendors Mega Menu]", error);
        this.renderEmpty();
        return;
      }

      this.filteredVendors = this.allVendors;
      this.vendorsByLetter = this.createLetterMap(this.allVendors);
      this.activeLetter = this.getInitialLetter();
      this.render();
    }

    bindEvents() {
      this.searchEl.addEventListener("input", this.handleSearch, {passive: true});
      this.azEl.addEventListener("click", this.handleAlphabetClick);
      if (this.backEl) {
        this.backEl.addEventListener("click", this.handleBackClick);
      }

      const detailsEl = this.root.closest("details");
      if (detailsEl) {
        detailsEl.addEventListener("toggle", this.handleMenuToggle);
      }
    }

    handleMenuToggle(event) {
      const detailsEl = event.currentTarget;
      if (!detailsEl || !detailsEl.open) return;

      // Always start from default state when the mega menu is opened.
      this.activeLetter = this.getInitialLetter();
      this.searchQuery = "";
      if (this.searchEl) this.searchEl.value = "";
      this.filteredVendors = this.allVendors;
      this.root.classList.remove("vendors-mega--letter-active");
      this.scheduleRender();
    }

    handleSearch(event) {
      this.searchQuery = String(event.target.value || "").trim().toLowerCase();
      this.activeLetter = "";
      this.root.classList.remove("vendors-mega--letter-active");
      this.filteredVendors = this.searchQuery
        ? this.allVendors.filter((vendor) => vendor.name.toLowerCase().includes(this.searchQuery))
        : this.allVendors;
      this.scheduleRender();
    }

    handleAlphabetClick(event) {
      const button = event.target.closest("[data-letter]");
      if (!button) return;
      if (typeof event.preventDefault === "function") event.preventDefault();

      const letter = button.dataset.letter;
      if (!letter) return;

      if (!this.searchQuery) {
        this.activeLetter = letter;
        this.scheduleRender();
        return;
      }

      if (!this.scrollEl) return;
      const target = this.contentEl.querySelector(`[data-group="${CSS.escape(letter)}"]`);
      if (!target) return;

      const downOffset = Math.round(this.scrollEl.clientHeight * 0.2);
      const scrollRect = this.scrollEl.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const targetTop = this.scrollEl.scrollTop + (targetRect.top - scrollRect.top) + downOffset;

      this.scrollEl.scrollTo({
        top: Math.max(0, targetTop),
        behavior: "smooth",
      });
    }

    handleBackClick() {
      this.activeLetter = "";
      this.searchQuery = "";
      if (this.searchEl) this.searchEl.value = "";
      this.filteredVendors = this.allVendors;
      this.root.classList.remove("vendors-mega--letter-active");
      this.scheduleRender();
    }

    scheduleRender() {
      if (this.renderRaf) cancelAnimationFrame(this.renderRaf);
      this.renderRaf = requestAnimationFrame(() => this.render());
    }

    createLetterMap(vendors) {
      const map = new Map(LETTERS.map((letter) => [letter, []]));

      for (const vendor of vendors) {
        const firstChar = vendor.name.charAt(0).toUpperCase();
        const letter = LETTERS.includes(firstChar) ? firstChar : "";
        if (!letter) continue;
        map.get(letter).push(vendor);
      }

      return map;
    }

    getInitialLetter() {
      const hasDefault = (this.vendorsByLetter.get(DEFAULT_LETTER) || []).length > 0;
      if (hasDefault) return DEFAULT_LETTER;
      const firstWithItems = LETTERS.find((letter) => (this.vendorsByLetter.get(letter) || []).length > 0);
      return firstWithItems || DEFAULT_LETTER;
    }

    groupVendors(vendors) {
      const groups = new Map();

      for (const vendor of vendors) {
        const firstChar = vendor.name.charAt(0).toUpperCase();
        const letter = LETTERS.includes(firstChar) ? firstChar : "#";

        if (!groups.has(letter)) groups.set(letter, []);
        groups.get(letter).push(vendor);
      }

      const ordered = [...groups.entries()].sort((a, b) => {
        if (a[0] === "#") return 1;
        if (b[0] === "#") return -1;
        return a[0].localeCompare(b[0]);
      });

      return ordered;
    }

    render() {
      if (!this.searchQuery) {
        this.renderAlphabetMode();
        return;
      }

      const grouped = this.groupVendors(this.filteredVendors);
      if (!grouped.length) {
        this.renderEmpty(true);
        return;
      }

      const azHtml = grouped
        .map(
          ([letter]) => `<a href="#" class="vendors-mega__az-link" data-letter="${letter}">${letter}</a>`
        )
        .join("");

      const groupsHtml = grouped
        .map(([letter, vendors]) => {
          const items = vendors
            .map(
              (vendor) =>
                `<a class="vendors-mega__item" href="${escapeHTML(vendor.url)}" title="${escapeHTML(vendor.name)}">${escapeHTML(vendor.name)}</a>`
            )
            .join("");

          return `
            <section class="vendors-mega__group" data-group="${letter}">
              <h4 class="vendors-mega__group-title">${letter}</h4>
              <div class="vendors-mega__list">${items}</div>
            </section>
          `;
        })
        .join("");

      this.root.classList.remove("vendors-mega--letter-active");
      if (this.backEl) this.backEl.hidden = true;
      this.azEl.innerHTML = azHtml;
      this.contentEl.innerHTML = `<div class="vendors-mega__groups">${groupsHtml}</div>`;
    }

    renderAlphabetMode() {
      const azHtml = LETTERS.map((letter) => {
        const isActive = this.activeLetter === letter;
        const classes = ["vendors-mega__az-link"];
        if (isActive) classes.push("is-active");
        if (this.activeLetter && !isActive) classes.push("is-collapsed");
        return `<a href="#" class="${classes.join(" ")}" data-letter="${letter}">${letter}</a>`;
      })
        .join("");

      this.azEl.innerHTML = azHtml;

      if (!this.activeLetter) {
        this.root.classList.remove("vendors-mega--letter-active");
        if (this.backEl) this.backEl.hidden = true;
        this.contentEl.innerHTML = `<div class="vendors-mega__placeholder" aria-hidden="true"></div>`;
        return;
      }

      const vendors = this.vendorsByLetter.get(this.activeLetter) || [];
      const columnCount = 8;
      const rowCount = Math.max(1, Math.ceil(vendors.length / columnCount));
      const placeholderCount = Math.max(0, (columnCount * rowCount) - vendors.length);
      const items = vendors
        .map(
          (vendor) =>
            `<a class="vendors-mega__item" href="${escapeHTML(vendor.url)}" title="${escapeHTML(vendor.name)}">${escapeHTML(vendor.name)}</a>`
        )
        .concat(
          Array.from(
            {length: placeholderCount},
            () => '<span class="vendors-mega__item vendors-mega__item--placeholder" aria-hidden="true"></span>'
          )
        )
        .join("");

      this.root.classList.add("vendors-mega--letter-active");
      if (this.backEl) this.backEl.hidden = false;

      if (!vendors.length) {
        this.contentEl.innerHTML = `<p class="vendors-mega__status">${escapeHTML(this.emptyText)}</p>`;
        return;
      }

      this.contentEl.innerHTML = `
        <div class="vendors-mega__groups vendors-mega__groups--single">
          <section class="vendors-mega__group" data-group="${this.activeLetter}">
            <h4 class="vendors-mega__group-title">${this.activeLetter}</h4>
            <div class="vendors-mega__list vendors-mega__list--single" style="--vendors-single-columns:${columnCount};--vendors-single-rows:${rowCount};">${items}</div>
          </section>
        </div>
      `;
    }

    renderEmpty(isSearchMode = false) {
      this.root.classList.remove("vendors-mega--letter-active");
      this.azEl.innerHTML = "";
      if (this.backEl) this.backEl.hidden = true;
      if (!isSearchMode) {
        this.contentEl.innerHTML = `<div class="vendors-mega__placeholder" aria-hidden="true"></div>`;
        return;
      }
      this.contentEl.innerHTML = `<p class="vendors-mega__status">${escapeHTML(this.emptyText)}</p>`;
    }
  }

  const mount = (scope = document) => {
    const nodes = scope.querySelectorAll(SELECTOR);
    nodes.forEach((node) => {
      if (node.dataset.vendorsMounted === "true") return;
      node.dataset.vendorsMounted = "true";
      const instance = new VendorsMegaMenu(node);
      instance.init();
    });
  };

  const mountIfMatch = (node) => {
    if (!(node instanceof Element)) return;

    if (node.matches(SELECTOR)) {
      mount(node.parentElement || document);
      return;
    }

    if (node.querySelector(SELECTOR)) {
      mount(node);
    }
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => mount());
  } else {
    mount();
  }

  document.addEventListener("shopify:section:load", (event) => {
    mount(event.target);
  });

  document.addEventListener("section:cached", () => {
    mount();
  });

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        mountIfMatch(node);
      }
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
